import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Users, Search, Info } from 'lucide-react';
import OverallCohortChart from './OverallCohortChart';
import LinesOfFailure from './LinesOfFailure';
import GlossaryLink from './common/GlossaryLink';
import { RBAC_ENDPOINTS } from '../config/api';
import { rbacFetch } from '../utils/apiClient';
import { fetchORRData } from '../services/survivalAnalysisService';

// ─── Tooltip ─────────────────────────────────────────────────────────────────
const Tooltip = ({ text, children }) => (
  <div className="relative group inline-flex items-center">
    {children}
    {/* Positioned to the right to avoid overflow clipping from parent containers */}
    <div className="
      pointer-events-none absolute left-full top-1 -translate-y-1/2 ml-2 z-[9999]
      w-max max-w-[250px] px-2.5 py-1.5 rounded-lg
      bg-slate-800 text-white text-[10px] leading-snug font-normal
      opacity-0 group-hover:opacity-100 transition-opacity duration-150
      shadow-lg whitespace-normal
    ">
      {text}
      {/* Left arrow */}
      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
    </div>
  </div>
);

// ─── Safety Profile ───────────────────────────────────────────────────────────
const SAFETY_FIELDS = [
  { key: 'crs',                   label: 'CRS',                   hasGrade: true,  tooltip: 'Cytokine Release Syndrome'                              },
  { key: 'icans',                 label: 'ICANS',                 hasGrade: true,  tooltip: 'Immune Effector Cell-Associated Neurotoxicity Syndrome'  },
  { key: 'iec_hs',                label: 'IEC-HS',                hasGrade: false, tooltip: null },
  // { key: 'icaht', label: 'ICAHT', hasGrade: false }, // hidden — data is null, re-enable when backend provides data
  { key: 'cytopenia',             label: 'Cytopenia (Grade III-IV)', hasGrade: false, tooltip: null },
  { key: 'hypogammaglobulinemia', label: 'Hypogammaglobulin...', hasGrade: false, tooltip: 'Hypogammaglobulinemia' },
];

// Lines of Therapy tooltip map
const LOT_TOOLTIPS = {
  '1':  '1 prior line of therapy given before receiving NexCAR19',
  '2':  '2 prior lines of therapy given before receiving NexCAR19',
  '3':  '3 prior lines of therapy given before receiving NexCAR19',
  '3+': '3 or more prior lines of therapy given before receiving NexCAR19',
};

const LOT_COLORS = ['bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-red-500'];

// ─── Patient table column definitions ────────────────────────────────────────
const baseFrozenCols = [
  { id: 'sno',        label: 'S.No.',      width: 50  },
  { id: 'pid',        label: 'IA Patient ID', width: 220 },
  { id: 'name',       label: 'Name',       width: 140 },
  { id: 'doctor',     label: 'Physician',  width: 150 },
];
const safetyCols = [
  { id: 'age',        label: 'Age',        width: 70  },
  { id: 'hid',        label: 'Hospital',   width: 160 },
  { id: 'crs',        label: 'CRS (Gr.)',              width: 110, gradeKey: 'crsGrade'   },
  { id: 'icans',      label: 'ICANS (Gr.)',             width: 120, gradeKey: 'icansGrade' },
  { id: 'iecHs',      label: 'IEC-HS',                width: 90  },
  { id: 'hypoIg',     label: 'Hypogammaglobulin...',   width: 160, tooltip: 'Hypogammaglobulinemia' },
  { id: 'cytopenia',  label: 'Cytopenia\n(Grade III-IV)', width: 130, multiLine: true },
  { id: 'aliveStatus', label: 'Alive/Deceased',            width: 100 },
  // { id: 'dateOfDeath', label: 'Date of Death',        width: 120 }, // Hidden per client request — may re-enable in future
];
const efficacyBoth = [
  { id: 'age',        label: 'Age',        width: 70  },
  { id: 'hid',        label: 'Hospital',   width: 160 },
  { id: 'diseaseStatus', label: 'R/R', width: 130 },
  { id: 'lastRx',    label: 'Prior LoT',      width: 100 },
  // { id: 'bridgeRcvd', label: 'Bridging\nTherapy Received', width: 140, multiLine: true }, // Hidden per client request — re-enable when needed
  // { id: 'bridgeRsp', label: 'Bridging\nTherapy Response', width: 155, multiLine: true }, // Hidden per client request — re-enable when needed
  { id: 'subType',   label: 'Type of Lymphoma', width: 140 },
  // IPI & Bulk Disease inserted here for NHL (see dynamicColumns logic)
  { id: 'infusion',  label: 'Infusion',       width: 90  },
  { id: 'd28',       label: 'D28',             width: 110 },
  { id: 'mrd',       label: 'Prior MRD Status',  width: 130 },
  { id: 'm3',        label: 'M3',             width: 60  },
  { id: 'm6',        label: 'M6',             width: 60  },
  { id: 'm9',        label: 'M9',             width: 60  },
  { id: 'm12',       label: 'M12',            width: 60  },
  { id: 'm15',       label: 'M15',            width: 60  },
  { id: 'm18',       label: 'M18',            width: 60  },
  { id: 'm21',       label: 'M21',            width: 60  },
  { id: 'm24',       label: 'M24',            width: 60  },
  { id: 'm30',       label: 'M30',            width: 60  },
  { id: 'm42',       label: 'M42',            width: 60  },
  { id: 'm48',       label: 'M48',            width: 60  },
  { id: 'aliveStatus', label: 'Alive/Deceased', width: 100 },
  { id: 'dateOfDeath', label: 'Date of Death', width: 120 },
];
const efficacyNHL = [
  { id: 'ipi',  label: 'Indicative IPI',          width: 140  },
  { id: 'bulk', label: 'Bulk', width: 100 },
];
const efficacyALL = [
  { id: 'blast', label: 'Blast %', width: 80 },
];

// ─── Segmented control ────────────────────────────────────────────────────────
const SegmentedControl = ({ options, value, onChange, label, icon: Icon }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
      {Icon && <Icon size={12} />}{label}
    </span>
    <div className="flex bg-gray-100/80 p-1 rounded-lg border border-gray-200 shadow-inner w-fit">
      {options.map(opt => (
        <button key={opt} onClick={() => onChange(opt)}
          className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
            value === opt
              ? 'bg-white text-blue-700 shadow-sm border border-gray-200/50'
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200/50 border border-transparent'
          }`}>
          {opt}
        </button>
      ))}
    </div>
  </div>
);

// ─── Safety Profile Section ───────────────────────────────────────────────────
export const SafetyProfileSection = ({ bucket }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, [bucket]);

  if (!bucket) return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
      <h3 className="text-sm font-bold text-slate-700 tracking-wider mb-4 uppercase">Safety Profile <GlossaryLink section="safety" tooltip="Incidence of the following adverse events among EE (efficacy-evaluable) patients: Cytokine Release Syndrome (CRS), Immune Effector Cell-Associated Neurotoxicity Syndrome (ICANS), Immune Effector Cell-Associated Hemophagocytic Lymphohistiocytosis-like Syndrome (IEC-HS), Cytopenia, and Hypogammaglobulinemia." /></h3>
      <p className="text-xs text-slate-400">No safety data available for this persona.</p>
    </div>
  );
  const n = bucket.n || 1;
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
      <h3 className="text-sm font-bold text-slate-700 tracking-wider mb-6 uppercase flex items-center gap-2">
        Safety Profile <GlossaryLink section="safety" tooltip="Incidence of the following adverse events among EE (efficacy-evaluable) patients: Cytokine Release Syndrome (CRS), Immune Effector Cell-Associated Neurotoxicity Syndrome (ICANS), Immune Effector Cell-Associated Hemophagocytic Lymphohistiocytosis-like Syndrome (IEC-HS), Cytopenia, and Hypogammaglobulinemia." />
      </h3>
      <div className="space-y-4">
        {SAFETY_FIELDS.map(field => {
          const data = bucket[field.key];
          if (!data) return null;
          const yesCount = data.incidence_count ?? 0;
          const gr3Count = data.grade3_plus_count ?? 0;
          const yesWidth = n > 0 ? (yesCount / n) * 100 : 0;
          const gr3Width = yesCount > 0 ? (gr3Count / yesCount) * 100 : 0;
          const lowWidth = yesCount > 0 ? ((yesCount - gr3Count) / yesCount) * 100 : 0;
          return (
            <div key={field.key} className="flex items-center gap-3 text-sm font-medium">
              <div className="w-40 flex-shrink-0 text-slate-600 text-xs overflow-visible whitespace-nowrap">
                {field.tooltip ? (
                  <Tooltip text={field.tooltip}>
                    <span className="border-b border-dashed border-slate-400 cursor-default">{field.label}</span>
                  </Tooltip>
                ) : (
                  <span title={field.label}>{field.label}</span>
                )}
              </div>
              <div className="flex-1 h-3 bg-slate-100 rounded-full flex overflow-hidden min-w-0">
                <div
                  style={{
                    width: mounted ? `${yesWidth}%` : '0%',
                    transition: `width 0.7s ease-out ${SAFETY_FIELDS.findIndex(f => f.key === field.key) * 0.1}s`,
                  }}
                  className="h-full flex rounded-full overflow-hidden"
                >
                  {yesCount > 0 && (
                    <>
                      <div style={{ width: `${lowWidth}%` }} className="h-full bg-yellow-400" />
                      {field.hasGrade && <div style={{ width: `${gr3Width}%` }} className="h-full bg-orange-500" />}
                    </>
                  )}
                </div>
              </div>
              <div className="w-36 flex-shrink-0 text-right text-xs whitespace-nowrap">
                <span className="font-bold text-slate-800">Yes: {yesCount ?? '—'}</span>
                {field.hasGrade && (
                  <><span className="text-slate-300 mx-1">|</span><span className="text-slate-500">Grade ≥3: {gr3Count ?? '—'}</span></>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Lines of Therapy Section ─────────────────────────────────────────────────
export const LinesOfTherapySection = ({ linesData, analysisType = 'PFS', totalN = null, timeline = '12 Month' }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, [linesData]);

  const lines = useMemo(() => {
    if (!linesData || linesData.length === 0) return null;
    // Show all lines including those with 0 patients
    const active = linesData;
    if (active.length === 0) return null;
    const labelMap = { '1': '1 Prior (2L)', '2': '2 Prior (3L)', '3': '3 Prior (4L)', '3+': '3+ Prior (4L+)' };

    // Build raw lines with counts
    const rawLines = active.map((line, idx) => {
      // NEW SIMPLIFIED LOGIC: Backend now always returns full 0-24 month data.
      // Check at_risk at evaluation timepoint (12 months) to determine hourglass vs 0%.
      const kmData = line.kmData;
      let timepointReached = true;

      if (kmData && kmData.x && kmData.at_risk) {
        const evalTimepoint = 12;
        const tpIdx = kmData.x.indexOf(evalTimepoint);
        if (tpIdx !== -1 && kmData.at_risk[tpIdx] === 0) {
          const censoredAtTp = kmData.censored ? kmData.censored[tpIdx] : 0;
          const eventsAtTp = kmData.events ? kmData.events[tpIdx] : 0;
          if (censoredAtTp > 0 && eventsAtTp === 0) {
            // at_risk=0, censored>0, events=0 → hourglass
            timepointReached = false;
          }
          // If censored=0, it's genuine 0% — timepointReached stays true
        }
      }

      /* ─── OLD TRUNCATION-BASED LOGIC (commented out — backend no longer truncates) ───
      if (kmData && kmData.x && kmData.x.length > 0) {
        const maxTime = Math.max(...kmData.x);
        const evalTimepoint = 12;
        if (maxTime < evalTimepoint) {
          const lastAtRisk = kmData.at_risk && kmData.at_risk.length > 0 ? kmData.at_risk[kmData.at_risk.length - 1] : null;
          const lastCensored = kmData.censored && kmData.censored.length > 0 ? kmData.censored[kmData.censored.length - 1] : null;
          const lastEvents = kmData.events && kmData.events.length > 0 ? kmData.events[kmData.events.length - 1] : null;
          const n = line.n || kmData.n || 0;
          if (lastAtRisk === 0 && lastCensored === 0 && lastEvents > 0) { timepointReached = true; }
          else if (lastAtRisk === 0 && lastCensored > 0) { timepointReached = false; }
          else if (lastEvents != null && lastEvents === n) { timepointReached = true; }
          else if (lastAtRisk != null && lastAtRisk > 0 && lastCensored === 0) { timepointReached = true; }
          else { timepointReached = false; }
        }
      }
      ─── END OLD LOGIC ─── */

      return {
        label:         labelMap[line.id] || line.label,
        tooltip:       LOT_TOOLTIPS[line.id] || null,
        count:         line.n || 0,
        survivalRate:  line.survival_rate ?? 0,
        color:         LOT_COLORS[idx] || 'bg-slate-400',
        timepointReached,
      };
    });

    // Normalize counts to match totalN (EE patients) if provided
    // This ensures Lines of Therapy total matches the persona's n value
    if (totalN && totalN > 0) {
      const rawTotal = rawLines.reduce((sum, l) => sum + l.count, 0);
      if (rawTotal > totalN) {
        // Proportionally scale down counts to sum to totalN
        const scale = totalN / rawTotal;
        let remaining = totalN;
        rawLines.forEach((line, idx) => {
          if (idx === rawLines.length - 1) {
            line.count = remaining; // last one gets the remainder to avoid rounding issues
          } else {
            line.count = Math.round(line.count * scale);
            remaining -= line.count;
          }
        });
      }
    }

    return rawLines;
  }, [linesData, totalN]);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
      <div className="mb-6">
        <h3 className="text-sm font-bold text-slate-700 tracking-wider uppercase">Lines of Therapy <GlossaryLink section="lines" tooltip={`${analysisType}% at ${timeline.replace(' Month', ' months')}, stratified by number of prior lines of therapy received.`} /></h3>
        {/* <p className="text-xs text-slate-400 mt-0.5">Survival outcomes by prior lines of therapy</p> */}
      </div>
      {!lines ? (
        // Subtle loading state — less jarring than skeleton bars
        <div className="space-y-4">
          {[70, 45, 25, 15].map((w, i) => (
            <div key={i} className="flex items-center gap-4 opacity-30">
              <div className="w-28 h-2.5 bg-slate-200 rounded-full" />
              <div className="flex-1 h-2.5 bg-slate-100 rounded-full" />
              <div className="w-16 h-2.5 bg-slate-200 rounded-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {lines.map((line, idx) => (
            <div key={line.label} className="flex items-center gap-4 text-sm font-medium">
              <div className="w-28 text-slate-600 text-xs">
                {line.tooltip ? (
                  <Tooltip text={line.tooltip}>
                    <span className="border-b border-dashed border-slate-400 cursor-default">{line.label}</span>
                  </Tooltip>
                ) : (
                  line.label
                )}
              </div>
              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  style={{ width: mounted ? `${line.survivalRate}%` : '0%', transition: `width 0.7s ease-out ${idx * 0.12}s` }}
                  className={`h-full ${line.color} rounded-full`}
                />
              </div>
              <div className="w-20 text-right text-xs">
                {line.count === 0 ? (
                  // No patients in this line category
                  <span className="text-slate-400">— <span className="ml-1">(0)</span></span>
                ) : !line.timepointReached && (line.survivalRate === 0 || line.survivalRate >= 100) ? (
                  // Patients exist but haven't reached evaluation timepoint
                  <span className="flex items-center justify-end gap-1 text-slate-500">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 shrink-0"><path d="M5 22h14"></path><path d="M5 2h14"></path><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path></svg>
                    <span className="text-slate-400">({line.count})</span>
                  </span>
                ) : (
                  // Normal display
                  <>
                    <span className="font-bold text-slate-800">{line.survivalRate}%</span>
                    <span className="text-slate-400 ml-1">({line.count})</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Inline Patient Table (also exported for dashboard use) ──────────────────
export const InlinePatientTable = ({
  indication,
  role,
  personaLabel   = null,
  hospital       = null,
  filterPhysician = null,
  activeFilters  = null,  // full filters object — used for geographic region scoping
  inModal        = false,  // true → no maxHeight, let parent scroll; false → internal scroll
}) => {
  const [patients, setPatients]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [dataScope, setDataScope] = useState('Efficacy');
  const [search, setSearch]       = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey]     = useState(null);   // column id currently sorted
  const [sortDir, setSortDir]     = useState('asc');  // 'asc' | 'desc'
  const tableScrollRef = useRef(null);

  const SEARCH_THRESHOLD = 10; // show search bar only when patient count exceeds this

  const cohortFilter = indication === 'B-ALL' ? 'ALL' : 'NHL';

  // Debounce search input — delays filtering by 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Filter patients by debounced search term — searches across ALL columns
  const filteredPatients = useMemo(() => {
    if (!debouncedSearch.trim()) return patients;
    const q = debouncedSearch.trim().toLowerCase();
    return patients.filter(p =>
      Object.values(p).some(val =>
        val != null && String(val).toLowerCase().includes(q)
      )
    );
  }, [patients, debouncedSearch]);

  // Sort filtered patients based on active sort key/direction
  const sortedPatients = useMemo(() => {
    if (!sortKey) return filteredPatients;
    const sorted = [...filteredPatients].sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];

      // Nulls/empty always go to the bottom regardless of direction
      const aEmpty = valA == null || String(valA).trim() === '' || String(valA).trim() === '-';
      const bEmpty = valB == null || String(valB).trim() === '' || String(valB).trim() === '-';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      // Try numeric comparison
      const numA = parseFloat(String(valA).replace(/[^0-9.\-]/g, ''));
      const numB = parseFloat(String(valB).replace(/[^0-9.\-]/g, ''));
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortDir === 'asc' ? numA - numB : numB - numA;
      }

      // Alphabetical comparison (case-insensitive)
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      const cmp = strA.localeCompare(strB);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredPatients, sortKey, sortDir]);

  // Handle column header click for sorting
  const handleSort = (colId) => {
    if (colId === 'sno') return; // S.No. is just row index, no sorting needed
    if (sortKey === colId) {
      // Same column clicked — toggle direction, then reset
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        // Reset sort
        setSortKey(null);
        setSortDir('asc');
      }
    } else {
      setSortKey(colId);
      setSortDir('asc');
    }
  };

  // Sort indicator component
  const SortIndicator = ({ colId }) => {
    if (sortKey !== colId) {
      return <span className="ml-1 text-gray-300 group-hover/th:text-gray-400 transition-colors">⇅</span>;
    }
    return (
      <span className="ml-1 text-blue-600 font-bold">
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  useEffect(() => {
    async function fetchPatients() {
      setLoading(true);
      setError(null);
      try {
        const ind = cohortFilter.toLowerCase();
        // Build URL manually with explicit encoding to ensure | chars are preserved
        let url = `${RBAC_ENDPOINTS.patientDeepDive}?indication=${encodeURIComponent(ind)}`;
        if (personaLabel)    url += `&persona=${encodeURIComponent(personaLabel)}`;
        if (hospital)        url += `&hospital=${encodeURIComponent(hospital)}`;
        if (filterPhysician) url += `&physician=${encodeURIComponent(filterPhysician)}`;

        // ── Geographic region filter — pass zone when active level is 'region' ──
        if (activeFilters?.activeLevel === 'region' && activeFilters?.selectedRegion) {
          url += `&zone=${encodeURIComponent(activeFilters.selectedRegion)}`;
        }

        const res = await rbacFetch(url);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setPatients(data.patients || []);
        setTotal(data.total || 0);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchPatients();
  }, [cohortFilter, personaLabel, hospital, filterPhysician, activeFilters?.activeLevel, activeFilters?.selectedRegion]);

  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = 0;
    setSortKey(null);
    setSortDir('asc');
  }, [dataScope]);

  const frozenColumns = useMemo(() => {
    // Physician sees Name, not PID. All other roles see PID, not Name.
    // Physician does NOT see Doctor column (they only see their own patients).
    const isPhysician = role === 'physician';
    const visibleCols = baseFrozenCols.filter(col => {
      if (col.id === 'name') return isPhysician;  // Name only for physician
      if (col.id === 'pid')  return !isPhysician; // PID only for non-physician
      if (col.id === 'doctor') return !isPhysician; // Physician doesn't need to see their own name
      return true;
    });

    // Add cohort-specific frozen columns (Efficacy mode only)
    let allFrozen = [...visibleCols];
    if (dataScope === 'Efficacy') {
      if (cohortFilter === 'NHL') {
        allFrozen.push(
          { id: 'diseaseStatus', label: 'R/R', width: 130 },
          { id: 'ipi',  label: 'Indicative IPI', width: 140 },
          { id: 'bulk', label: 'Bulk', width: 160 },
        );
      } else if (cohortFilter === 'ALL') {
        allFrozen.push(
          { id: 'diseaseStatus', label: 'R/R', width: 130 },
          { id: 'lastRx', label: 'Prior LoT', width: 100 },
          { id: 'blast', label: 'Blast %', width: 80 },
          { id: 'mrd', label: 'Prior MRD Status', width: 150 },
        );
      }
    }

    let left = 0;
    return allFrozen.map((col, i) => {
      const c = { ...col, left, isLast: i === allFrozen.length - 1 };
      left += col.width;
      return c;
    });
  }, [role, hospital, dataScope, cohortFilter]);

  const dynamicColumns = useMemo(() => {
    if (dataScope === 'Safety') return safetyCols;

    // IDs that are now in frozen columns — exclude from dynamic
    const frozenIds = new Set(frozenColumns.map(c => c.id));

    if (cohortFilter === 'NHL') {
      // Insert IPI & Bulk Disease right after Subtype
      const subtypeIdx = efficacyBoth.findIndex(c => c.id === 'subType');
      const cols = [...efficacyBoth].filter(c => c.id !== 'mrd'); // MRD not applicable for NHL
      cols.splice(subtypeIdx + 1, 0, ...efficacyNHL);
      return cols.filter(c => !frozenIds.has(c.id));
    }
    if (cohortFilter === 'ALL') {
      // B-ALL column order: Prior LoT → Blast % → Prior MRD Status → Infusion → D28 → M3...
      const cols = efficacyBoth.filter(c => c.id !== 'subType');
      // Remove mrd from current position (bridgeRcvd/bridgeRsp already hidden)
      const withoutRearranged = cols.filter(c => c.id !== 'bridgeRcvd' && c.id !== 'bridgeRsp' && c.id !== 'mrd');
      // Find lastRx index and insert in correct order after it
      const lastRxIdx = withoutRearranged.findIndex(c => c.id === 'lastRx');
      const blastCol = efficacyALL[0]; // { id: 'blast', label: 'Blast %' }
      const mrdCol = cols.find(c => c.id === 'mrd');
      // Insert: Blast %, MRD after Prior LoT (bridgeRcvd/bridgeRsp hidden per client request)
      const insertCols = [blastCol, mrdCol].filter(Boolean);
      withoutRearranged.splice(lastRxIdx + 1, 0, ...insertCols);
      return withoutRearranged.filter(c => !frozenIds.has(c.id));
    }
    return efficacyBoth.filter(c => !frozenIds.has(c.id));
  }, [dataScope, cohortFilter, frozenColumns]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden w-full min-w-0">

      {/* Slim control bar */}
      <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-1">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-gray-800">Infused Patient Details</span>
            {/* Data Scope toggle */}
            <div className="flex bg-gray-100/80 p-0.5 rounded-lg border border-gray-200 shadow-inner">
              {['Efficacy', 'Safety'].map(opt => (
                <button key={opt} onClick={() => setDataScope(opt)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                    dataScope === opt
                      ? 'bg-white text-blue-700 shadow-sm border border-gray-200/50'
                      : 'text-gray-500 hover:text-gray-800 border border-transparent'
                  }`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          {/* Search bar — only shown when patient count exceeds threshold */}
          {!loading && total > SEARCH_THRESHOLD && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search across all columns..."
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 w-64"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        </div>
        {/* Subtitle — patient count */}
        {!loading && total > 0 && (
          <p className="text-xs text-gray-500 mt-1">Patient-level detail for the current selection — <b>{total} patients</b></p>
        )}
      </div>
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-7 h-7 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!loading && error && (
        <div className="text-center py-8">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}
      {!loading && !error && patients.length === 0 && (
        <div className="text-center py-8">
          <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">
            {role === 'physician'
              ? 'No patients available for the selected physician.'
              : 'No patients match the selected criteria.'
            }
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && patients.length > 0 && (
        <div ref={tableScrollRef}
          className="overflow-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-gray-100 w-full"
          style={inModal
            ? { maxWidth: '100%', maxHeight: '350px' }
            : total > SEARCH_THRESHOLD
              ? { maxHeight: 'calc(100vh - 480px)', minHeight: '200px', maxWidth: '100%' }
              : { maxWidth: '100%' }
          }>
          <table className="w-full text-left bg-white min-w-max" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                {frozenColumns.map(col => (
                  <th key={col.id}
                    onClick={() => handleSort(col.id)}
                    className={`sticky text-center top-0 z-30 bg-gray-100 border-b-2 border-b-gray-300 px-3 py-3 text-[11px] font-bold text-gray-700 tracking-wider whitespace-nowrap group/th
                      ${col.id === 'sno' ? 'normal-case' : 'uppercase'}
                      ${col.id !== 'sno' ? 'cursor-pointer hover:bg-gray-200 select-none' : ''}
                      ${col.isLast ? 'border-r-2 border-r-gray-300 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : 'border-r border-r-gray-200'}`}
                    style={{ left: col.left, position: 'sticky', minWidth: col.width, maxWidth: col.width }}>
                    {col.id === 'ipi'
                      ? <>Indicative IPI<sup className="text-[13px] align-super leading-none">*</sup></>
                      : col.label}
                    {col.id !== 'sno' && <SortIndicator colId={col.id} />}
                  </th>
                ))}
                {dynamicColumns.map(col => (
                  <th key={col.id}
                    onClick={() => handleSort(col.id)}
                    className={`sticky text-center top-0 z-20 bg-gray-100 border-b-2 border-b-gray-300 border-r border-r-gray-200 px-3 py-2 text-[11px] font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 select-none group/th ${col.multiLine ? 'whitespace-normal leading-tight' : 'whitespace-nowrap'}`}
                    style={{ minWidth: col.width }}
                    title={col.tooltip || undefined}>
                    {col.tooltip ? (
                      <span className="border-b border-dashed border-gray-400 cursor-pointer">{col.label}</span>
                    ) : col.multiLine ? (
                      col.label.split('\n').map((line, i) => <span key={i} className="block">{line}</span>)
                    ) : (
                      col.label
                    )}
                    <SortIndicator colId={col.id} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedPatients.length === 0 ? (
                <tr>
                  <td colSpan={frozenColumns.length + dynamicColumns.length} className="text-center py-8 text-gray-400 text-sm">
                    No patients match &ldquo;{search}&rdquo;
                  </td>
                </tr>
              ) : sortedPatients.map((p, idx) => (
                <tr key={p.pid || idx} className="group hover:bg-blue-50/50 transition-colors">
                  {frozenColumns.map(col => (
                    <td key={col.id}
                      className={`sticky z-10 text-center bg-white group-hover:bg-blue-50/90 px-3 py-2 text-[12px] text-gray-800 whitespace-nowrap
                        ${col.isLast ? 'border-r-2 border-r-gray-300 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : 'border-r border-r-gray-100'}`}
                      style={{ left: col.left, position: 'sticky', minWidth: col.width, maxWidth: col.width }}>
                      {col.id === 'sno' && (idx + 1)}
                      {col.id === 'pid' && <span className="font-mono text-[11px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{p.pid || '—'}</span>}
                      {col.id === 'name' && (p.name || '—')}
                      {col.id === 'doctor' && <span className="truncate block max-w-[140px]" title={p.doctor}>{p.doctor || '—'}</span>}
                      {col.id === 'indication' && (
                        <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${
                          (p.indication || '').toLowerCase().includes('lymphoma') ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                        }`}>
                          {(p.indication || '').toLowerCase().includes('lymphoma') ? 'NHL' : 'ALL'}
                        </span>
                      )}
                      {!['sno', 'pid', 'name', 'doctor', 'indication'].includes(col.id) && (p[col.id] || <span className="text-gray-300">-</span>)}
                    </td>
                  ))}
                  {dynamicColumns.map(col => {
                    const value = p[col.id] || null;
                    const strVal = value ? String(value) : '';
                    // Capitalize yes/no from CSV
                    const baseDisplay = strVal.toLowerCase() === 'yes' ? 'Yes'
                      : strVal.toLowerCase() === 'no' ? 'No'
                      : value;

                    // For CRS/ICANS — combine status + grade into one cell: "Yes (Gr. 2)"
                    const grade = col.gradeKey ? (p[col.gradeKey] || null) : null;
                    const displayValue = (baseDisplay === 'Yes' && grade)
                      ? `Yes (Gr. ${grade})`
                      : baseDisplay;

                    const isHighRisk = dataScope === 'Safety' && strVal.toLowerCase() === 'yes' && grade && (Number(grade) >= 3);
                    const isYes = dataScope === 'Safety' && strVal.toLowerCase() === 'yes';
                    const isCR = dataScope === 'Efficacy' && strVal.toUpperCase() === 'CR';
                    const isPR = dataScope === 'Efficacy' && strVal.toUpperCase() === 'PR';
                    const isPD = dataScope === 'Efficacy' && strVal.toUpperCase() === 'PD';
                    const isSD = dataScope === 'Efficacy' && strVal.toUpperCase() === 'SD';
                    return (
                      <td key={col.id}
                        className={`border-r text-center border-r-gray-100 px-3 py-2 text-[12px] whitespace-nowrap
                          ${isHighRisk ? 'text-red-700 font-semibold bg-red-50' : ''}
                          ${!isHighRisk && isYes ? 'text-amber-700 bg-amber-50/30' : ''}
                          ${isCR ? 'text-emerald-700 font-semibold' : ''}
                          ${isPR ? 'text-blue-600 font-semibold' : ''}
                          ${isPD ? 'text-red-600 font-semibold' : ''}
                          ${isSD ? 'text-amber-600 font-semibold' : ''}
                          ${!isHighRisk && !isYes && !isCR && !isPR && !isPD && !isSD ? 'text-gray-700' : ''}
                          bg-white group-hover:bg-blue-50/50`}
                        style={{ minWidth: col.width }}>
                        {displayValue || <span className="text-gray-300">-</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Note */}
      <div className="mx-4 my-3 px-3 py-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-xs text-slate-700">
          <span className="text-sm font-semibold">*</span> Indicative IPI is an approximate International Prognostic Index derived from available patient data.
        </p>
        <p className="text-xs text-slate-700 mt-1">
          <span className="font-bold">Note:</span> The patient list contains information updated on 15-06-2026.
        </p>
      </div>
    </div>
  );
};

// ─── Helper: should the patient table be shown for this role + filter level ──
// Sales & Physician: hidden at global (Overall) and region (Geographic) levels
// Superadmin & Stakeholder: always shown
function shouldShowPatientTable(role, activeLevel) {
  if (role === 'sales' || role === 'physician') {
    return activeLevel === 'institute' ||
           activeLevel === 'physician'  ||
           activeLevel === 'my_patients';
  }
  return true; // superadmin, stakeholder — always show
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function PersonaDeepDiveModal({
  isOpen, onClose,
  selectedPersona, selectedCard,
  overallCohortData, linesOfFailureData, onLinesDataLoaded,
  safetyData,
  filters, indication, analysisType, timeline, gradientScheme,
  rbacConfig, physicianName,
}) {
  const [personaOrr, setPersonaOrr] = useState(null);

  // Reset ORR when persona changes
  useEffect(() => {
    setPersonaOrr(null);
  }, [selectedPersona?.persona]);

  // Fetch ORR for this persona
  useEffect(() => {
    if (!isOpen || !selectedPersona?.persona) return;
    let cancelled = false;
    fetchORRData(filters, indication).then(result => {
      if (cancelled) return;
      if (result?.persona_orr_objects?.length > 0) {
        const match = result.persona_orr_objects.find(p => p.label === selectedPersona.persona);
        if (match) setPersonaOrr(match.orr_percent);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, selectedPersona?.persona, indication, filters]);

  const handleLinesLoaded = (data) => {
    if (onLinesDataLoaded) onLinesDataLoaded(data);
  };

  const safetyBucket = useMemo(() => {
    if (!safetyData?.persona_buckets || !selectedPersona?.persona) return null;
    return safetyData.persona_buckets.find(b => b.label === selectedPersona.persona) || null;
  }, [safetyData, selectedPersona]);

  const headerStats = useMemo(() => {
    if (!selectedPersona) return null;
    const kmData = selectedPersona.kmData;
    const timelineMonths = parseInt((timeline || '12 Month').split(' ')[0]);
    let survivalRate = null, medianTime = null;
    if (kmData?.x && kmData?.y) {
      const idx = kmData.x.findIndex(t => t === timelineMonths);
      if (idx !== -1) {
        // Check if at_risk is 0 at this timepoint
        const atRiskAtTimepoint = kmData.at_risk ? kmData.at_risk[idx] : null;
        if (atRiskAtTimepoint === 0) {
          // Only show 0 (hourglass) when censored > 0 AND events = 0
          // If events > 0, the KM survival % is meaningful — show actual percentage
          const censoredAtTp = kmData.censored ? kmData.censored[idx] : 0;
          const eventsAtTp = kmData.events ? kmData.events[idx] : 0;
          if (censoredAtTp > 0 && eventsAtTp === 0) {
            survivalRate = 0; // Data doesn't reach this timepoint — hourglass
          } else {
            survivalRate = Math.round(kmData.y[idx] * 100);
          }
        } else {
          survivalRate = Math.round(kmData.y[idx] * 100);
        }
      } else {
        // Timepoint not in x array — curve was truncated before this month
        // Determine if this is genuine 0% or hourglass
        const lastAtRisk = kmData.at_risk && kmData.at_risk.length > 0 ? kmData.at_risk[kmData.at_risk.length - 1] : null;
        const lastCensored = kmData.censored && kmData.censored.length > 0 ? kmData.censored[kmData.censored.length - 1] : null;
        const lastEvents = kmData.events && kmData.events.length > 0 ? kmData.events[kmData.events.length - 1] : null;
        const n = kmData.n || selectedPersona.patients || 0;

        if (lastAtRisk === 0 && lastCensored === 0 && lastEvents > 0) {
          // All patients had events → genuine 0%
          survivalRate = 0;
        } else if (lastEvents != null && lastEvents === n) {
          // All patients had events (truncated) → genuine 0%
          survivalRate = 0;
        } else if (lastAtRisk != null && lastAtRisk > 0 && lastCensored === 0) {
          // All remaining at risk with no censoring — will all have events → genuine 0%
          survivalRate = 0;
        } else {
          // Patients censored before reaching timepoint → hourglass (null keeps it hidden)
          survivalRate = null;
        }
      }
      const medIdx = kmData.y.findIndex(v => v <= 0.5);
      medianTime = medIdx !== -1 ? kmData.x[medIdx]?.toFixed(1) : 'Timepoint Not Reached';
    }
    // Format title: replace " | " with " • "
    const displayTitle = (selectedPersona.persona || selectedPersona.title || '')
      .replace(/ \| /g, ' • ');
    return { n: selectedPersona.patients, survivalRate, medianTime, displayTitle };
  }, [selectedPersona, timeline]);

  if (!isOpen || !selectedPersona) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 md:p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-7xl rounded-[24px] shadow-2xl flex flex-col overflow-hidden"
        style={{ height: '92vh' }}>

        {/* Header */}
        <div className="bg-[#1e293b] text-white px-8 py-5 flex justify-between items-start shrink-0">
          <div>
            <h2 className="text-xl font-bold tracking-wide mb-2">
              {headerStats?.displayTitle || 'Persona Deep Dive'}
            </h2>
            <div className="flex flex-wrap gap-5 items-center text-sm text-slate-300">
              {headerStats?.n != null && (
                <span className="bg-white/10 px-3 py-1 rounded text-white text-xs font-semibold">
                  n = {headerStats.n}
                </span>
              )}
              {personaOrr != null ? (
                <span className="text-xs">ORR = {Math.round(personaOrr)}%</span>
              ) : (
                <span className="inline-block w-16 h-3 bg-white/20 rounded-full animate-pulse" />
              )}
              {headerStats?.survivalRate != null && (
                <span className="text-xs">
                  {parseInt((timeline || '12 Month').split(' ')[0])}M {analysisType}: {headerStats.survivalRate}%
                </span>
              )}
              {headerStats?.medianTime && (
                <span className="text-xs">
                  Median {analysisType}: {headerStats.medianTime === 'Timepoint Not Reached' ? headerStats.medianTime : `${headerStats.medianTime} mo`}
                </span>
              )}
              <span className="text-xs opacity-50">{indication} · {analysisType} · {timeline}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-slate-300 hover:text-white ml-4">
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-6 space-y-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">

          {/* Section 1: Lines of Therapy + Safety Profile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LinesOfTherapySection linesData={linesOfFailureData} totalN={selectedPersona?.patients || null} analysisType={analysisType} timeline={timeline} />
            <SafetyProfileSection bucket={safetyBucket} />
          </div>

          {/* Section 2: KM Curve + Risk Table + Hospital Distribution */}
          <OverallCohortChart
            key={selectedPersona?.persona}
            indication={indication}
            analysisType={analysisType}
            timeline={timeline}
            gradientScheme={gradientScheme}
            overallCohortData={overallCohortData}
            selectedPersonaData={selectedPersona}
            linesOfFailureData={linesOfFailureData}
            onResetToOverall={null}
            filters={filters}
            selectedCard={selectedCard}
            onLinesDataLoaded={handleLinesLoaded}
            onHospitalDataReady={null}
            hideHospitalDistribution={
              (rbacConfig?.role === 'sales' || rbacConfig?.role === 'physician') &&
              filters?.activeLevel !== 'global' &&
              filters?.activeLevel !== 'region'
            }
            userRole={rbacConfig?.role}
          />

          {/* Section 3: Patient Details — hidden for Sales/Physician at global & region levels */}
          {shouldShowPatientTable(rbacConfig?.role, filters?.activeLevel) && (
            <InlinePatientTable
              key={selectedPersona?.persona || 'no-persona'}
              indication={indication}
              role={rbacConfig?.role}
              inModal={true}
              personaLabel={selectedPersona?.persona || null}
              hospital={filters?.activeLevel === 'institute' ? (filters?.selectedInstitute || null) : null}
              filterPhysician={
                filters?.physicianMode ? (filters?.myPhysicianName || null) :
                filters?.activeLevel === 'my_patients' ? (filters?.myPhysicianName || null) :
                filters?.activeLevel === 'physician' ? (filters?.selectedPhysician || null) :
                // Physician role at hospital level — scope to own patients only
                (filters?.activeLevel === 'institute' && rbacConfig?.role === 'physician' && (physicianName || filters?.myPhysicianName)) ? (physicianName || filters?.myPhysicianName) :
                null
              }
              activeFilters={filters}
            />
          )}
        </div>
      </div>
    </div>
  );
}
